import React from 'react';
import { useFitnessWizard } from '../../contexts/FitnessWizardContext';
import { ThemedButton } from '../ThemedButton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ShieldCheck, AlertCircle } from 'lucide-react';

const FitnessFraudWarranty = () => {
  const { formData, updateFormData, nextStep } = useFitnessWizard();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.fraud_warranty_accepted) {
      updateFormData({ fraud_warranty_timestamp: new Date().toISOString() });
      nextStep();
    }
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="bg-[#232323] text-white rounded-t-xl">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-6 w-6" />
          <div>
            <CardTitle className="text-xl">Fraud Warranty Statement</CardTitle>
            <CardDescription className="text-white/60">
              Please review and accept the fraud warranty
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Warning Banner */}
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                <strong>Important:</strong> You must read and accept the fraud warranty statement below to proceed with your application.
              </p>
            </div>
          </div>

          {/* Fraud Warranty Text */}
          <div className="p-6 bg-slate-50 border border-slate-200 rounded-lg max-h-64 overflow-y-auto">
            <h4 className="font-semibold text-slate-900 mb-3">Fraud Warning Statement</h4>
            <div className="text-sm text-slate-700 space-y-3">
              <p>
                Any person who knowingly and with intent to defraud any insurance company or other person files an application for insurance or statement of claim containing any materially false information, or conceals for the purpose of misleading, information concerning any fact material thereto, commits a fraudulent insurance act, which is a crime and subjects such person to criminal and civil penalties.
              </p>
              <p>
                <strong>Notice to Applicants:</strong> By signing this application, you are stating that all information provided is true and correct to the best of your knowledge. Any misrepresentation or omission of material facts may result in denial of coverage or voiding of the policy.
              </p>
              <p>
                I understand that the insurance company will rely on the information contained in this application and all other information being submitted. I represent that the information provided is true and correct.
              </p>
              <p>
                I authorize the release of claim information from any prior insurer necessary to process this application.
              </p>
            </div>
          </div>

          {/* Acceptance Checkbox */}
          <div className={`p-4 rounded-lg border-2 transition-all ${
            formData.fraud_warranty_accepted 
              ? 'border-green-500 bg-green-50' 
              : 'border-slate-200'
          }`}>
            <div className="flex items-start gap-3">
              <Checkbox
                id="fraud_warranty"
                data-testid="fraud-warranty-checkbox"
                checked={formData.fraud_warranty_accepted}
                onCheckedChange={(checked) => updateFormData({ fraud_warranty_accepted: checked })}
                className="mt-0.5"
              />
              <label 
                htmlFor="fraud_warranty" 
                className="text-sm cursor-pointer select-none"
              >
                <span className={`font-medium ${
                  formData.fraud_warranty_accepted ? 'text-green-900' : 'text-slate-900'
                }`}>
                  I have read, understand, and agree to the Fraud Warranty Statement above.
                </span>
                <span className="block text-slate-600 mt-1">
                  I certify that all information provided in this application is true and accurate to the best of my knowledge.
                </span>
              </label>
            </div>
          </div>

          <ThemedButton 
            type="submit"
            data-testid="fitness-fraud-next-btn"
            disabled={!formData.fraud_warranty_accepted}
            variant="wizard" size="full"
          >
            Next: Additional Insured
          </ThemedButton>
        </form>
      </CardContent>
    </Card>
  );
};

export default FitnessFraudWarranty;
